
import { GeoJSONFeature } from 'maplibre-gl';
import { geojsonTypes, modes, events } from './utils/constants';
import lineSplit from '@turf/line-split';
import flatten from '@turf/flatten';
import { drawModes, drawState } from '../../Types';

const SplitLineMode = <drawModes>{};

type SplitLineState = drawState & {
  main: any[];
  sourceIds: string[];
  startedDraw?: boolean;
};

function isLineGeometry(type: unknown): boolean {
  return type === 'LineString' || type === 'MultiLineString';
}

SplitLineMode.onSetup = function () {
  const selected = this.getSelected();
  const main = selected
    .map((f: any) => {
      const gj = f.toGeoJSON?.() ?? f;
      const id = gj?.id ?? f?.id ?? gj?.properties?.id ?? f?.properties?.id;
      return {
        ...gj,
        id: id != null ? id : gj?.id,
      };
    })
    .filter((f: any) => isLineGeometry(f.geometry?.type));

  if (main.length < 1) {
    throw new Error('Please select a line feature to split.');
  }

  const sourceIds = main
    .map((f: any) => String(f.id ?? ''))
    .filter((id: string) => id && id !== 'undefined');

  const selectedIds = selected
    .map((f: any) => String(f?.id ?? ''))
    .filter((id: string) => id && id !== 'undefined');

  return {
    main,
    sourceIds: selectedIds.length > 0 ? selectedIds : sourceIds,
    startedDraw: false,
  } as SplitLineState;
};

SplitLineMode.toDisplayFeatures = function (
  state: SplitLineState,
  geojson: GeoJSONFeature,
  display: (feature: GeoJSONFeature) => void
) {
  display(geojson);

  if (state.startedDraw) return;
  state.startedDraw = true;

  this.changeMode('passing_mode_line_string', (cut: any) => {
    (SplitLineMode as any).applyCut.call(this, state, cut);
  });
};

(SplitLineMode as any).applyCut = function (state: SplitLineState, cut: any) {
  const finishFailed = (reason: string) => {
    try {
      this.map.fire('draw.split.failed', { reason });
    } catch {
      /* ignore */
    }
    try {
      this.changeMode(modes.SIMPLE_SELECT, { featureIds: state.sourceIds });
    } catch {
      /* ignore */
    }
  };

  if (cut?.geometry?.type !== 'LineString') {
    finishFailed('invalidCut');
    return;
  }

  const pieces: any[] = [];

  try {
    for (const mainFeature of state.main) {
      flatten(mainFeature).features.forEach((feature: any) => {
        if (!isLineGeometry(feature.geometry?.type)) return;
        const afterCut = lineSplit(feature, cut);
        if (afterCut.features.length >= 2) {
          for (const part of afterCut.features) {
            if (part.geometry?.type === 'LineString') {
              pieces.push({
                type: 'Feature',
                properties: { ...(mainFeature.properties ?? {}) },
                geometry: part.geometry,
              });
            }
          }
        }
      });
    }
  } catch {
    finishFailed('invalidCut');
    return;
  }

  if (pieces.length < 2) {
    finishFailed('noSplit');
    return;
  }

  (SplitLineMode as any).commitSplit.call(this, state, state.sourceIds, pieces);
};

(SplitLineMode as any).commitSplit = function (
  state: SplitLineState,
  sourceIds: string[],
  pieces: any[]
) {
  const ids = [
    ...new Set(
      [...sourceIds, ...(state.sourceIds ?? [])]
        .map((id) => String(id ?? ''))
        .filter((id) => id && id !== 'undefined')
    ),
  ];

  for (const id of ids) {
    try {
      this.deleteFeature(String(id), { silent: true });
    } catch {
      /* ignore */
    }
  }

  try {
    for (const f of this.getAll?.()?.features ?? []) {
      const fid = String(f?.id ?? '');
      if (ids.includes(fid)) {
        this.deleteFeature(fid, { silent: true });
      }
    }
  } catch {
    /* ignore */
  }

  const created: any[] = [];
  const newIds: string[] = [];

  for (const piece of pieces) {
    const properties = { ...(piece.properties ?? {}) };
    delete properties.__tempFeatureId;
    delete properties.id;
    try {
      const newId = this._ctx.api.add({
        type: 'Feature',
        properties,
        geometry: piece.geometry,
      });
      if (newId != null) {
        newIds.push(String(newId));
        const added = this.getFeature(String(newId));
        if (added) created.push(added.toGeoJSON());
      }
    } catch {
      /* ignore */
    }
  }

  if (newIds.length < 2) {
    try {
      this.map.fire('draw.split.failed', { reason: 'invalidCut' });
    } catch {
      /* ignore */
    }
    try {
      this.changeMode(modes.SIMPLE_SELECT, { featureIds: state.sourceIds });
    } catch {
      /* ignore */
    }
    return;
  }

  try {
    this.map.fire(events.UPDATE, {
      action: 'SplitLine',
      features: created,
    });
    this.map.fire('draw.split', { count: newIds.length, featureIds: newIds });
  } catch {
    /* ignore */
  }

  try {
    this.changeMode(modes.SIMPLE_SELECT, { featureIds: newIds });
  } catch {
    this.changeMode?.(modes.SIMPLE_SELECT);
  }
};

export default SplitLineMode;
